const SOURCE_LABELS = Object.freeze({
    user_provided: 'Informado pelo usuário',
    internal_data: 'Dados internos',
    database_lookup: 'Consulta ao Thalamus',
    web_research: 'Pesquisa web',
    historical_analysis: 'Análise histórica',
    ai_inferred: 'Inferido pela IA',
    missing: 'Sem fonte',
})

const STATE_LABELS = Object.freeze({
    known: 'Conhecido',
    estimated: 'Estimativa',
    suggested: 'Sugerido',
    pending: 'Pendente de definição',
    missing: 'Ausente',
    contested: 'Contestado',
})

const DATE_FIELD_PATHS = new Set([
    'dtInicio',
    'dtEstimadaFinal',
    'dt_prevista_conclusao',
    'dt_conclusao',
])

const ENUM_FIELD_PATHS = new Set(['nivel', 'finalidade', 'area'])

const FIELD_LABELS = Object.freeze({
    nome: 'Nome',
    nivel: 'Nível',
    finalidade: 'Finalidade',
    area: 'Área',
    setor_id: 'Setor aprovador',
    setorDemanda_id: 'Setor demandante',
    setores_envolvidos: 'Setores envolvidos',
    dtInicio: 'Data de abertura',
    dtEstimadaFinal: 'Previsão de conclusão',
    gerentePCM_id: 'Gerente responsável',
    objetivo_geral: 'Objetivo geral',
    descricao_problema: 'Problema a ser solucionado',
    objetivos_estrategicos: 'Objetivos estratégicos',
    ganho_operacional_esperado: 'Resultado esperado',
    escopo: 'Escopo',
    resultados: 'Entregas',
    impacto_viabilidade: 'Impactos e viabilidade',
    custo_justificativa: 'Justificativa do custo',
})

function object(value) {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {}
}

function fieldBase(path) {
    const segments = String(path || '').split('.')
    return segments[segments.length - 1] || ''
}

function isDateField(path) {
    return DATE_FIELD_PATHS.has(fieldBase(path))
}

function isEnumField(path) {
    return ENUM_FIELD_PATHS.has(fieldBase(path))
}

export function resolvePmoFieldState(item = {}) {
    return item.resolution_state
        || item.resolutionState
        || item.state
        || 'missing'
}

function metadataFor(path, item, provenance) {
    const meta = object(provenance[path])
    const resolutionState = item.resolution_state
        ?? item.resolutionState
        ?? meta.resolution_state
        ?? meta.resolutionState
        ?? null
    return {
        source: item.source ?? meta.source ?? 'missing',
        evidence_ids: Array.isArray(item.evidence_ids)
            ? item.evidence_ids
            : (Array.isArray(meta.evidence_ids) ? meta.evidence_ids : []),
        confidence: item.confidence ?? meta.confidence ?? null,
        resolution_state: resolutionState,
        state: resolutionState || item.state || meta.state || 'missing',
        requires_confirmation: item.requires_confirmation ?? meta.requires_confirmation ?? false,
        reason: item.reason ?? meta.reason ?? '',
    }
}

function presentPmoField(path, item, provenance = {}) {
    const field = {
        path,
        label: FIELD_LABELS[fieldBase(path)] || FIELD_LABELS[path] || path,
        value: item.value,
        ...metadataFor(path, item, object(provenance)),
    }
    const resolvedState = resolvePmoFieldState(field)
    return {
        ...field,
        state: resolvedState,
        displayValue: formatPmoResolvedValue(field),
        stateLabel: pmoStateLabel(resolvedState),
    }
}

export function pmoFieldEntries(draft, fieldProvenance = {}) {
    if (Array.isArray(draft)) {
        return draft.map((raw) => {
            const item = object(raw)
            const path = item.path || item.field_path || item.fieldPath || ''
            return presentPmoField(path, item, fieldProvenance)
        })
    }
    return Object.entries(object(draft)).map(([path, raw]) => {
        const wrapped = object(raw)
        const hasValue = Object.prototype.hasOwnProperty.call(wrapped, 'value')
        return presentPmoField(path, hasValue ? wrapped : { value: raw }, fieldProvenance)
    })
}

export function pmoSourceLabel(source) {
    return SOURCE_LABELS[source] || source || 'Sem fonte'
}

export function pmoStateLabel(state) {
    return STATE_LABELS[state] || state || 'Ausente'
}

export function formatPmoValue(value) {
    if (value === undefined || value === null || value === '') return 'Não informado'
    if (typeof value === 'boolean') return value ? 'Sim' : 'Não'
    if (Array.isArray(value)) {
        if (!value.length) return 'Nenhum item'
        return value.map((item) => {
            if (item === null || item === undefined) return ''
            if (typeof item !== 'object') return String(item)
            return item.nome || item.descricao || item.titulo || JSON.stringify(item)
        }).filter(Boolean).join(' • ')
    }
    if (typeof value === 'object') return JSON.stringify(value, null, 2)
    return String(value)
}

export function normalizePmoCitation(evidence) {
    const item = object(evidence)
    return {
        id: item.id || item.evidence_id || item.uuid || '',
        title: item.title || item.titulo || item.name || item.url || 'Evidência',
        url: item.url || item.link || item.source_url || null,
        excerpt: item.excerpt || item.snippet || item.content || item.summary || '',
        source: item.source || item.source_type || item.type || '',
        retrieved_at: item.retrieved_at || item.retrievedAt || item.collected_at || null,
    }
}

export function formatPmoDate(value) {
    if (!value) return ''
    const date = new Date(value)
    return Number.isNaN(date.getTime())
        ? String(value)
        : new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(date)
}

export function formatPmoDateValue(value) {
    if (!value) return ''
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-')
        return `${day}/${month}/${year}`
    }
    const date = new Date(value)
    return Number.isNaN(date.getTime())
        ? String(value)
        : new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(date)
}

export function formatPmoResolvedValue(item = {}) {
    const state = resolvePmoFieldState(item)
    const path = item.path || item.field_path || item.fieldPath || ''
    if (isDateField(path)) {
        if (state === 'pending') return 'Pendente de definição'
        if (state === 'estimated') {
            const formatted = formatPmoDateValue(item.value)
            return formatted ? `${formatted} Estimativa` : 'Estimativa'
        }
    }
    if (isEnumField(path) && state === 'suggested') {
        const formatted = formatPmoValue(item.value)
        return formatted === 'Não informado' ? 'Sugerido' : formatted
    }
    return formatPmoValue(item.value)
}

export function buildAssistantActionPreview({
    actionPreview = null,
    draft = null,
    fieldProvenance = {},
    fallback = null,
} = {}) {
    const preview = actionPreview && typeof actionPreview === 'object' ? actionPreview : null
    if (!preview && !fallback) return null
    const sourceFields = preview?.fields || draft
    const fields = sourceFields ? pmoFieldEntries(sourceFields, fieldProvenance) : []
    return {
        title: preview?.title || fallback?.title || 'Revisão disponível',
        description: preview?.description || fallback?.description || '',
        fields,
    }
}
